import React, { useState } from "react";
import { Button, Layout, Typography, Space, Card, Input, message } from "antd";
import { CheckCircleFilled, PrinterOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { resetFlow, selectCheckoutTotals, selectPosFlow } from "../store/posSlice";
import { printReceipt } from "../utils/receiptPrinter";
import axiosClient from "../api/axiosClient";

const { Content } = Layout;
const { Title, Text } = Typography;

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function DoneScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lastReceiptId, lastTransactionId, service, clientName } = useAppSelector(selectPosFlow);
  const { subtotal, tax, total, taxRate } = useAppSelector(selectCheckoutTotals);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  async function handlePrintReceipt() {
    await printReceipt(
      {
        receiptId: lastReceiptId ?? "",
        amountDue: total,
        cashReceived: total,
        changeDue: 0,
        paidAtIso: new Date().toISOString(),
        lineItems: service ? [{ name: service, price: subtotal }] : undefined,
        footerLines: taxRate > 0 ? [`Tax (${(taxRate * 100).toFixed(1)}%): ${formatMoney(tax)}`] : undefined,
      },
      { fallbackToBrowserPrint: true }
    );
  }

  async function handleEmailReceipt() {
    if (!lastTransactionId || !email) return;
    setSending(true);
    try {
      await axiosClient.post(
        `/transactions/${lastTransactionId}/email-receipt`,
        {
          to_email: email,
          subject: "Your receipt",
          body_text: `Thank you for your visit!\n\nService: ${service ?? "N/A"}\nSubtotal: ${formatMoney(subtotal)}\nTax: ${formatMoney(tax)}\nTotal: ${formatMoney(total)}\nTransaction: ${lastTransactionId}`,
        },
        { headers: authHeader() }
      );
      message.success("Receipt emailed.");
    } catch {
      message.error("Failed to email receipt.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Content style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ maxWidth: 520, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <CheckCircleFilled style={{ fontSize: 72, color: "#16a34a" }} />
            <Title level={2} style={{ marginTop: 16, marginBottom: 4 }}>
              Payment Successful!
            </Title>
            <Text type="secondary">Transaction completed successfully</Text>
          </div>

          <Card style={{ borderRadius: 16 }}>
            <div style={{ textAlign: "center", borderBottom: "1px solid #f0f0f0", paddingBottom: 16, marginBottom: 16 }}>
              <Text type="secondary">Amount Paid</Text>
              <Title style={{ marginTop: 4, fontSize: 48 }}>{formatMoney(total)}</Title>
            </div>

            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {clientName && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Client</Text>
                  <Text strong>{clientName}</Text>
                </div>
              )}
              {service && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Service</Text>
                  <Text strong>{service}</Text>
                </div>
              )}
              {taxRate > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">Subtotal</Text>
                    <Text>{formatMoney(subtotal)}</Text>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary">Tax ({(taxRate * 100).toFixed(1)}%)</Text>
                    <Text>{formatMoney(tax)}</Text>
                  </div>
                </>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">Transaction ID</Text>
                <Text strong style={{ fontFamily: "monospace" }}>
                  {lastTransactionId ?? "N/A"}
                </Text>
              </div>
            </Space>
          </Card>

          <div style={{ marginTop: 20 }}>
            <Button type="primary" block size="large" style={{ height: 52, borderRadius: 12, fontWeight: 600 }} onClick={() => navigate("/admin/sales")}>
              Back to Calendar
            </Button>

            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <Button icon={<PrinterOutlined />} block size="large" onClick={handlePrintReceipt}>
                Print Receipt
              </Button>
              <Button
                icon={<MailOutlined />}
                block
                size="large"
                loading={sending}
                disabled={!email}
                onClick={handleEmailReceipt}
              >
                Email Receipt
              </Button>
            </div>
            <Input
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ marginTop: 8 }}
            />

            <Button type="text" block style={{ marginTop: 12 }} onClick={() => dispatch(resetFlow())}>
              New Sale
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
}
